import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthRoutes } from './auth/AuthRoutes';
import { AUTH_PATHS } from './auth/authPaths';
import {
  EVENT_FORBIDDEN,
  EVENT_REGISTRATION_INCOMPLETE,
  EVENT_UNAUTHORIZED,
} from '../services/apiErrors';
import { PartnerForm } from './PartnerForm';
import { AppHeader } from './AppHeader';
import { BottomNav } from './BottomNav';
import { CabinetProvider } from '../contexts/CabinetContext';
import { NAV_PATHS } from './navigation/navItems';
import { SubscriptionBanner } from './SubscriptionBanner';
import { ActivationCard } from './ActivationCard';
import { HomePage } from './pages/HomePage';
import { CatalogPage } from './pages/CatalogPage';
import { CategoryItemsPage } from './pages/CategoryItemsPage';
import { DiscountsPage } from './pages/DiscountsPage';
import { PromoPage } from './pages/PromoPage';
import { QrCodesPage } from './pages/QrCodesPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { BillingPage } from './pages/BillingPage';
import { SupportPage } from './static/SupportPage';
import { TermsClientsPage } from './static/TermsClientsPage';
import { TermsPartnersPage } from './static/TermsPartnersPage';
import { UgcRulesPage } from './static/UgcRulesPage';
import { PrivacyPage } from './static/PrivacyPage';

export const App: React.FC = () => {
  const { isAuthenticated, logout, loadPartnerProfile, partner, requireRegistration } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Обработчик события unauthorized для перенаправления на логин
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log('Перенаправляем на логин после 401 ошибки');
      navigate(AUTH_PATHS.login);
    };

    const handleForbidden = () => {
      console.log('Перенаправляем на страницу ошибки после 403 ошибки');
      navigate('/forbidden');
    };

    // `409 registration_incomplete` — не ошибка, а штатное состояние: номер
    // подтверждён, анкета не отправлена. Возвращаем человека туда, где он
    // остановился; введённое лежит в черновике.
    const handleRegistrationIncomplete = () => {
      requireRegistration();
      navigate(AUTH_PATHS.registerDetails);
    };

    window.addEventListener(EVENT_UNAUTHORIZED, handleUnauthorized);
    window.addEventListener(EVENT_FORBIDDEN, handleForbidden);
    window.addEventListener(EVENT_REGISTRATION_INCOMPLETE, handleRegistrationIncomplete);

    return () => {
      window.removeEventListener(EVENT_UNAUTHORIZED, handleUnauthorized);
      window.removeEventListener(EVENT_FORBIDDEN, handleForbidden);
      window.removeEventListener(EVENT_REGISTRATION_INCOMPLETE, handleRegistrationIncomplete);
    };
  }, [navigate, requireRegistration]);

  // Обновление профиля при изменении роута (для актуализации данных тарифа)
  useEffect(() => {
    if (isAuthenticated) {
      console.log('Роут изменился, обновляем профиль');
      loadPartnerProfile();
    }
  }, [location.pathname, isAuthenticated, loadPartnerProfile]);

  const navTabs = NAV_PATHS;
  const tab = navTabs.findIndex((path) => location.pathname.startsWith(path) && (path === '/' ? location.pathname === '/' : true));

  // Обработчики для переходов
  // Раздел формы можно открыть сразу: чек-лист запуска ведёт к точке на карте.
  const handleEdit = (section?: string) =>
    navigate(section ? `/edit?section=${encodeURIComponent(section)}` : '/edit');
  const handleLogout = () => { logout(); navigate(AUTH_PATHS.login); };

  // Публичные страницы — доступны и без авторизации, и с ней
  if (location.pathname === '/support') {
    return <SupportPage />;
  }
  if (location.pathname === '/terms/clients') {
    return <TermsClientsPage />;
  }
  if (location.pathname === '/terms/partners') {
    return <TermsPartnersPage />;
  }
  if (location.pathname === '/terms') {
    return <Navigate to="/terms/clients" replace />;
  }
  // Адрес зашит в мобильное приложение (строка согласия под кнопкой отправки
  // отзыва) и уйдёт в Notes for Review — менять его нельзя.
  if (location.pathname === '/ugc-rules') {
    return <UgcRulesPage />;
  }
  // Privacy Policy URL — обязательное поле App Store Connect; адрес уходит в
  // метаданные стора и в меню приложения, менять его нельзя.
  if (location.pathname === '/privacy') {
    return <PrivacyPage />;
  }

  // Кабинет закрыт: лендинг, развилка, две двери и анкета регистрации.
  if (!isAuthenticated) {
    return <AuthRoutes />;
  }

  // Аутентифицированный пользователь
  const activeTab = tab === -1 ? 0 : tab;
  return (
    <CabinetProvider>
      <div className="qp-shell">
        <AppHeader />
        <main className="qp-main">
          {partner?.subscription?.is_activated === false && partner.subscription.activation && (
            <ActivationCard activation={partner.subscription.activation} onEdit={handleEdit} />
          )}
          <SubscriptionBanner />
          <Routes>
            <Route path="/" element={<HomePage onEdit={() => handleEdit()} onEditLocation={() => handleEdit('location')} onEditLegal={() => handleEdit('legal')} onLogout={handleLogout} />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/catalog/category/:categoryId" element={<CategoryItemsPage />} />
            <Route path="/discounts" element={<DiscountsPage />} />
            <Route path="/promo" element={<PromoPage />} />
            <Route path="/qr" element={<QrCodesPage />} />
            <Route path="/edit" element={<PartnerForm onSuccess={() => navigate('/')} onCancel={() => navigate('/')} />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        {/* Разделы на телефоне живут внизу: второй ярус шапки там скрыт. */}
        <BottomNav value={activeTab} onChange={(idx) => navigate(navTabs[idx])} />
      </div>
    </CabinetProvider>
  );
};
